// Noms des notes. Système français (do ré mi) ou anglais (C D E).
// Dans l'interface, une erreur d'octave est décrite en mots (« une octave trop haut »)
// pour éviter la confusion entre la convention française (do3 = do central) et la
// convention scientifique (C4 = do central).

export type NamingSystem = 'fr' | 'en';

const NAMES_FR = ['do', 'do♯', 'ré', 'ré♯', 'mi', 'fa', 'fa♯', 'sol', 'sol♯', 'la', 'la♯', 'si'];
const NAMES_EN = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
const BLACK = new Set([1, 3, 6, 8, 10]);

export function pitchClass(pitch: number): number {
  return ((pitch % 12) + 12) % 12;
}

/** Octave scientifique : 60 → 4. */
export function octaveOf(pitch: number): number {
  return Math.floor(pitch / 12) - 1;
}

export function isBlackKey(pitch: number): boolean {
  return BLACK.has(pitchClass(pitch));
}

export function noteName(pitch: number, system: NamingSystem = 'fr'): string {
  return (system === 'fr' ? NAMES_FR : NAMES_EN)[pitchClass(pitch)];
}

/** Nom avec octave, dans la convention du système choisi (fr : do central = do3 ; en : C4). */
export function noteNameWithOctave(pitch: number, system: NamingSystem = 'fr'): string {
  const octave = system === 'fr' ? octaveOf(pitch) - 1 : octaveOf(pitch);
  return `${noteName(pitch, system)}${octave}`;
}

export function midiToFrequency(pitch: number): number {
  return 440 * Math.pow(2, (pitch - 69) / 12);
}

export function frequencyToMidiFloat(frequency: number): number {
  return 69 + 12 * Math.log2(frequency / 440);
}

/** Note MIDI la plus proche d'une fréquence et écart en cents. */
export function nearestPitch(frequency: number): { pitch: number; cents: number } {
  const midi = frequencyToMidiFloat(frequency);
  const pitch = Math.round(midi);
  return { pitch, cents: (midi - pitch) * 100 };
}

const LETTERS: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Analyse 'C4', 'C#4', 'Db3', 'A#2'. Notation scientifique (C4 = do central = 60). */
export function parsePitch(token: string): number {
  const m = /^([A-Ga-g])([#b]?)(-?\d)$/.exec(token.trim());
  if (!m) throw new Error(`hauteur illisible : "${token}"`);
  const letter = m[1].toUpperCase();
  const accidental = m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0;
  const octave = Number(m[3]);
  return (octave + 1) * 12 + LETTERS[letter] + accidental;
}
